import React, { useMemo } from "react";
import Select, { components } from "react-select";

const PRIMARY = "#49B7F8";
/** Sidebar + dashboard: control on black (#000) — charcoal surface, subtle gray border */
const SURFACE_CHARCOAL = "#2A2A2A";
const BORDER_CHARCOAL = `1px solid #3F3F3F`;
const BORDER_CHARCOAL_FOCUS = `1px solid #4F4F4F`;
const BORDER_CHARCOAL_HOVER = `1px solid #4a4a4a`;
const MENU_BG_CHARCOAL = "#252525";
const RADIUS_CHARCOAL = "6px";

const FolderIcon = ({ className }) => (
  <svg
    className={className}
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    aria-hidden
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z"
    />
  </svg>
);

function buildSelectStyles() {
  return {
    container: (base) => ({
      ...base,
      width: "100%",
    }),
    control: (base, state) => ({
      ...base,
      width: "100%",
      minWidth: 0,
      backgroundColor: SURFACE_CHARCOAL,
      border: state.isFocused ? BORDER_CHARCOAL_FOCUS : BORDER_CHARCOAL,
      borderRadius: RADIUS_CHARCOAL,
      minHeight: "44px",
      boxShadow: "0 1px 2px rgba(0, 0, 0, 0.4)",
      cursor: "pointer",
      "&:hover": {
        border: state.isFocused ? BORDER_CHARCOAL_FOCUS : BORDER_CHARCOAL_HOVER,
      },
    }),
    menu: (base) => ({
      ...base,
      backgroundColor: MENU_BG_CHARCOAL,
      border: BORDER_CHARCOAL,
      borderRadius: RADIUS_CHARCOAL,
      marginTop: 4,
      overflow: "hidden",
      boxShadow: "0 8px 24px rgba(0, 0, 0, 0.5)",
      zIndex: 10000,
    }),
    menuPortal: (base) => ({ ...base, zIndex: 10000 }),
    menuList: (base) => ({
      ...base,
      padding: "6px",
      maxHeight: "260px",
    }),
    option: (base, state) => ({
      ...base,
      cursor: "pointer",
      padding: "10px 12px",
      borderRadius: "4px",
      border:
        state.isFocused || state.isSelected
          ? "1px solid rgba(255, 255, 255, 0.18)"
          : "1px solid transparent",
      backgroundColor: state.isSelected
        ? "rgba(255, 255, 255, 0.06)"
        : state.isFocused
          ? "rgba(255, 255, 255, 0.04)"
          : "transparent",
      color: "#ffffff",
    }),
    singleValue: (base) => ({
      ...base,
      color: "#ffffff",
      marginLeft: 0,
      maxWidth: "calc(100% - 8px)",
    }),
    valueContainer: (base) => ({
      ...base,
      paddingLeft: "12px",
      paddingRight: "8px",
      paddingTop: "2px",
      paddingBottom: "2px",
      flexWrap: "nowrap",
      gap: "4px",
    }),
    input: (base) => ({
      ...base,
      color: "#ffffff",
      margin: 0,
      padding: 0,
      caretColor: "#ffffff",
    }),
    placeholder: (base) => ({
      ...base,
      color: "rgba(255, 255, 255, 0.45)",
      fontSize: "14px",
    }),
    dropdownIndicator: (base, state) => ({
      ...base,
      color: state.menuIsOpen
        ? "rgba(255, 255, 255, 0.6)"
        : "rgba(255, 255, 255, 0.42)",
      padding: "10px 12px 10px 4px",
      "&:hover": {
        color: "rgba(255, 255, 255, 0.7)",
      },
    }),
    indicatorSeparator: () => ({ display: "none" }),
  };
}

function CustomSingleValue(props) {
  return (
    <components.SingleValue {...props}>
      <div className="flex min-w-0 items-center gap-2">
        <FolderIcon className="h-4 w-4 shrink-0 text-primary" />
        <span className="font-secondary truncate text-sm font-medium text-white">
          {props.data.label}
        </span>
      </div>
    </components.SingleValue>
  );
}

function CustomDropdownIndicator(props) {
  const stroke = props.charcoalStroke ? "rgba(255, 255, 255, 0.45)" : PRIMARY;
  return (
    <components.DropdownIndicator {...props}>
      <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke={stroke}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden
      >
        <path d="M6 9l6 6 6-6" />
      </svg>
    </components.DropdownIndicator>
  );
}

function CustomOption(props) {
  const { data } = props;
  return (
    <components.Option {...props}>
      <div className="flex items-start gap-2.5">
        <FolderIcon className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
        <div className="min-w-0 flex-1 text-left">
          <div className="font-secondary truncate text-sm font-medium text-white">
            {data.label}
          </div>
        </div>
      </div>
    </components.Option>
  );
}

function filterProjectOption(option, rawInput) {
  if (!rawInput) return true;
  const q = rawInput.trim().toLowerCase();
  if (!q) return true;
  const label = String(option.label ?? "").toLowerCase();
  const desc = String(option.data?.description ?? "").toLowerCase();
  return label.includes(q) || desc.includes(q);
}

/**
 * Project picker. Sidebar + dashboard use charcoal control (#2A2A2A, border #3F3F3F).
 */
export default function ProjectSelect({
  projects,
  selectedProject,
  onSelect,
  variant = "sidebar",
}) {
  const options = useMemo(
    () =>
      projects.map((p) => ({
        value: p.id,
        label: p.name,
        description: p.description || "",
        project: p,
      })),
    [projects],
  );

  const value = useMemo(() => {
    if (!selectedProject || !options.length) return null;
    return (
      options.find((o) => String(o.value) === String(selectedProject.id)) ??
      null
    );
  }, [selectedProject, options]);

  const styles = useMemo(() => buildSelectStyles(), []);
  const instanceId =
    variant === "dashboard"
      ? "dashboard-project-select"
      : "sidebar-project-select";
  const charcoalStroke = variant === "sidebar" || variant === "dashboard";

  const DropdownIndicator = useMemo(
    () => (props) => (
      <CustomDropdownIndicator {...props} charcoalStroke={charcoalStroke} />
    ),
    [charcoalStroke],
  );

  return (
    <div className="w-full min-w-0">
      <Select
        instanceId={instanceId}
        classNamePrefix={
          variant === "dashboard"
            ? "dn-project-select-dashboard"
            : "dn-project-select"
        }
        className="font-secondary w-full min-w-0 text-left"
        options={options}
        value={value}
        onChange={(opt) => {
          if (opt?.project) onSelect(opt.project);
        }}
        isClearable={false}
        isSearchable
        filterOption={filterProjectOption}
        placeholder="Search or select project"
        noOptionsMessage={() => "No projects"}
        menuPlacement="auto"
        menuPosition="fixed"
        menuPortalTarget={
          typeof document !== "undefined" ? document.body : null
        }
        styles={styles}
        components={{
          SingleValue: CustomSingleValue,
          Option: CustomOption,
          DropdownIndicator,
        }}
      />
    </div>
  );
}
